import * as React from 'react';

import {
  announcementWriteErrorMessage,
  type AnnouncementDto,
  type AnnouncementListFilters,
  type AnnouncementStateDto,
} from '@/api/announcements-admin-client';
import {
  useAnnouncements,
  useCancelAnnouncement,
} from '@/api/use-announcements-admin';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Label } from '@/components/ui/label';
import {
  ANNOUNCEMENT_STATES,
  announcementStateLabel,
} from '@/lib/announcement-labels';
import { CguTabs, type CguTabDef } from '@/routes/admin/cgu/cgu-tabs';
import { AnnouncementComposer } from '@/routes/admin/communication/announcements-composer';
import {
  AnnouncementsRegistreTable,
  type AnnouncementSortColumn,
  type AnnouncementSortDirection,
} from '@/routes/admin/communication/announcements-registre-table';

// =============================================================================
// announcements-screen.tsx — Écran ADMIN « Annonces » (composer + registre), R15-COM-03 (DBU §2.7.3).
// =============================================================================
// Deux onglets accessibles (réutilise CguTabs, WAI-ARIA Tabs, 0 dépendance) :
//   (a) Composer  : nouvelle annonce OU édition d'une annonce ouverte depuis le registre ;
//   (b) Registre  : liste filtrable (statut / campagne / période) + triable (toutes colonnes) + annulation.
// L'état de vue (onglet, annonce sélectionnée, filtres) vit dans l'URL via l'adaptateur routeur (RULE_REF_006 :
// pas de localStorage). La présentation reste testable hors routeur (props d'état). Le tri est un état de VUE
// mineur, en mémoire (useState). Autorité = API : l'UI lit et habille ; aucune règle métier ici.

/** Identifiants stables des onglets (sérialisés en URL). */
export type AnnouncementsTab = 'composer' | 'registre';

const TABS: readonly CguTabDef[] = [
  { id: 'composer', label: 'Composer' },
  { id: 'registre', label: 'Registre' },
];

export interface AnnouncementsScreenProps {
  readonly activeTab: AnnouncementsTab;
  readonly selectedAnnouncementId: string | undefined;
  readonly filters: AnnouncementListFilters;
  readonly onSelectTab: (tab: AnnouncementsTab) => void;
  readonly onSelectAnnouncement: (id: string | undefined) => void;
  readonly onFiltersChange: (filters: AnnouncementListFilters) => void;
  /** Horloge injectable (fenêtre de modification / état effectif déterministes en test). */
  readonly nowFactory?: () => Date;
}

export function AnnouncementsScreen({
  activeTab,
  selectedAnnouncementId,
  filters,
  onSelectTab,
  onSelectAnnouncement,
  onFiltersChange,
  nowFactory,
}: AnnouncementsScreenProps): React.JSX.Element {
  return (
    <div className="space-y-8">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">Annonces</h1>
        <p className="text-muted-foreground">
          Diffusion globale d'annonces aux fournisseurs d'une campagne : composer,
          planification, fenêtre de modification et registre.
        </p>
      </header>

      <CguTabs
        tabs={TABS}
        activeId={activeTab}
        onSelect={(id) => onSelectTab(id as AnnouncementsTab)}
        ariaLabel="Sections des annonces"
      >
        {activeTab === 'composer' ? (
          <ComposerTab
            selectedAnnouncementId={selectedAnnouncementId}
            onSelectAnnouncement={onSelectAnnouncement}
            onGoToRegistre={() => onSelectTab('registre')}
            {...(nowFactory ? { nowFactory } : {})}
          />
        ) : (
          <RegistreTab
            filters={filters}
            onFiltersChange={onFiltersChange}
            onOpen={(id) => {
              onSelectAnnouncement(id);
              onSelectTab('composer');
            }}
            {...(nowFactory ? { nowFactory } : {})}
          />
        )}
      </CguTabs>
    </div>
  );
}

// --- Onglet (a) — Composer ---

function ComposerTab({
  selectedAnnouncementId,
  onSelectAnnouncement,
  onGoToRegistre,
  nowFactory,
}: {
  readonly selectedAnnouncementId: string | undefined;
  readonly onSelectAnnouncement: (id: string | undefined) => void;
  readonly onGoToRegistre: () => void;
  readonly nowFactory?: () => Date;
}): React.JSX.Element {
  // Résolution de l'annonce ouverte (deep-link/refresh) via le registre complet (autorité API).
  const all = useAnnouncements();
  const resolved: AnnouncementDto | null =
    selectedAnnouncementId === undefined
      ? null
      : (all.data?.find((a) => a.announcementId === selectedAnnouncementId) ??
        null);

  const isResolving =
    selectedAnnouncementId !== undefined && all.isPending;
  const notFound =
    selectedAnnouncementId !== undefined && !all.isPending && resolved === null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {selectedAnnouncementId === undefined
            ? 'Nouvelle annonce.'
            : 'Édition d’une annonce existante.'}
        </p>
        <div className="flex gap-2">
          {selectedAnnouncementId !== undefined ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onSelectAnnouncement(undefined)}
            >
              Nouvelle annonce
            </Button>
          ) : null}
          <Button type="button" variant="ghost" size="sm" onClick={onGoToRegistre}>
            Voir le registre
          </Button>
        </div>
      </div>

      {isResolving ? (
        <p role="status" className="text-muted-foreground">
          Chargement de l'annonce…
        </p>
      ) : notFound ? (
        <p role="alert" className="text-destructive">
          Annonce introuvable. Elle a peut-être été supprimée ou n'existe plus.
        </p>
      ) : (
        <AnnouncementComposer
          key={selectedAnnouncementId ?? 'new'}
          announcement={resolved}
          {...(nowFactory ? { nowFactory } : {})}
        />
      )}
    </div>
  );
}

// --- Onglet (b) — Registre ---

function RegistreTab({
  filters,
  onFiltersChange,
  onOpen,
  nowFactory,
}: {
  readonly filters: AnnouncementListFilters;
  readonly onFiltersChange: (filters: AnnouncementListFilters) => void;
  readonly onOpen: (id: string) => void;
  readonly nowFactory?: () => Date;
}): React.JSX.Element {
  const announcements = useAnnouncements(filters);
  const cancelMut = useCancelAnnouncement();

  const [sortColumn, setSortColumn] =
    React.useState<AnnouncementSortColumn>('sentAt');
  const [sortDirection, setSortDirection] =
    React.useState<AnnouncementSortDirection>('desc');
  const [toCancel, setToCancel] = React.useState<AnnouncementDto | null>(null);

  const onSort = React.useCallback((column: AnnouncementSortColumn): void => {
    setSortColumn((prevColumn) => {
      if (prevColumn === column) {
        setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        return prevColumn;
      }
      setSortDirection('asc');
      return column;
    });
  }, []);

  // Options de campagne du filtre : campagnes DISTINCTES effectivement présentes au registre (reflète les
  // données, pas seulement les éligibles — une annonce peut viser une campagne devenue inéligible depuis).
  const campaignOptions = React.useMemo(() => {
    const seen = new Map<string, string>();
    for (const a of announcements.data ?? []) {
      if (!seen.has(a.campaignId)) {
        seen.set(a.campaignId, a.campaignIntitule ?? a.campaignId);
      }
    }
    return [...seen.entries()].map(([campaignId, intitule]) => ({
      campaignId,
      intitule,
    }));
  }, [announcements.data]);

  const setFilter = React.useCallback(
    (patch: {
      state?: AnnouncementStateDto | undefined;
      campaignId?: string | undefined;
      from?: string | undefined;
      to?: string | undefined;
    }): void => {
      const merged = { ...filters, ...patch };
      // Nettoie les clés vides/undefined (URL propre) — reconstruit un filtre strict.
      const cleaned: {
        state?: AnnouncementStateDto;
        campaignId?: string;
        from?: string;
        to?: string;
      } = {};
      if (merged.state !== undefined) cleaned.state = merged.state;
      if (merged.campaignId !== undefined && merged.campaignId !== '')
        cleaned.campaignId = merged.campaignId;
      if (merged.from !== undefined && merged.from !== '') cleaned.from = merged.from;
      if (merged.to !== undefined && merged.to !== '') cleaned.to = merged.to;
      onFiltersChange(cleaned);
    },
    [filters, onFiltersChange],
  );

  const onConfirmCancel = React.useCallback((): void => {
    if (toCancel === null) {
      return;
    }
    cancelMut.mutate(toCancel.announcementId, {
      onSettled: () => setToCancel(null),
    });
  }, [toCancel, cancelMut]);

  return (
    <div className="space-y-4">
      {/* Filtres statut / campagne / période. Libellés associés (WCAG 1.3.1). */}
      <div
        className="flex flex-wrap items-end gap-4"
        role="group"
        aria-label="Filtres du registre des annonces"
      >
        <div className="space-y-1">
          <Label htmlFor="ann-filter-state">Statut</Label>
          <select
            id="ann-filter-state"
            value={filters.state ?? 'all'}
            onChange={(event) =>
              setFilter({
                state:
                  event.target.value === 'all'
                    ? undefined
                    : (event.target.value as AnnouncementStateDto),
              })
            }
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="all">Tous</option>
            {ANNOUNCEMENT_STATES.map((state) => (
              <option key={state} value={state}>
                {announcementStateLabel(state).label}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-1">
          <Label htmlFor="ann-filter-campaign">Campagne</Label>
          <select
            id="ann-filter-campaign"
            value={filters.campaignId ?? 'all'}
            onChange={(event) =>
              setFilter({
                campaignId:
                  event.target.value === 'all' ? undefined : event.target.value,
              })
            }
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="all">Toutes</option>
            {campaignOptions.map((campaign) => (
              <option key={campaign.campaignId} value={campaign.campaignId}>
                {campaign.intitule}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-1">
          <Label htmlFor="ann-filter-from">Envoyée depuis</Label>
          <input
            id="ann-filter-from"
            type="date"
            value={filters.from ?? ''}
            onChange={(event) => setFilter({ from: event.target.value })}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          />
        </div>

        <div className="space-y-1">
          <Label htmlFor="ann-filter-to">Envoyée jusqu'au</Label>
          <input
            id="ann-filter-to"
            type="date"
            value={filters.to ?? ''}
            onChange={(event) => setFilter({ to: event.target.value })}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          />
        </div>
      </div>

      {cancelMut.isError ? (
        <p role="alert" className="text-destructive">
          {announcementWriteErrorMessage(
            cancelMut.error,
            "L'annulation a échoué.",
          )}
        </p>
      ) : null}

      {announcements.isPending ? (
        <p role="status" className="text-muted-foreground">
          Chargement du registre…
        </p>
      ) : announcements.isError ? (
        <p role="alert" className="text-destructive">
          Erreur de chargement : {announcements.error.message}
        </p>
      ) : announcements.data.length === 0 ? (
        <p role="status" className="text-muted-foreground">
          Aucune annonce ne correspond aux filtres.
        </p>
      ) : (
        <AnnouncementsRegistreTable
          rows={announcements.data}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={onSort}
          onOpen={onOpen}
          onCancel={setToCancel}
          {...(nowFactory ? { now: nowFactory() } : {})}
        />
      )}

      <ConfirmDialog
        open={toCancel !== null}
        title="Annuler l'annonce planifiée ?"
        description={
          toCancel !== null
            ? `L'annonce « ${toCancel.titre} » ne sera pas diffusée. Cette action est définitive.`
            : ''
        }
        confirmLabel="Annuler l'annonce"
        cancelLabel="Revenir"
        confirmDisabled={cancelMut.isPending}
        onConfirm={onConfirmCancel}
        onCancel={() => setToCancel(null)}
      />
    </div>
  );
}
